import _ from 'lodash';

function calcWeight({ product }) {
    const val = (() => {
        if (product.type === 'Air freight' || product.type === 'LCL') {
            let total = 0;
            for (const calc of (product.calculations || [])) {
                total += (calc.weight || 0) * (calc.quantity || 1);
            }
            return total;
        }
        return (product.weight || 0) * (_.isUndefined(product.quantity) ? 1 : product.quantity);
    })();
    const postfix = product.kgOrLbs || 'kg';
    return { val, postfix };
}

function calcVolumeWeight({ product, transportationMethod, round }) {
    let volume = (() => {
        if (product.type === 'Air freight') {
            const cmOrInches = product.cmOrInches || 'cm';
            // volume in cm^3 (or in^3 depending on cmOrInches)
            let total = 0;
            for (const calc of (product.calculations || [])) {
                const qty = calc.quantity || 1;
                if (calc.method === 'dimensions') {
                    total += (calc.length || 0) * (calc.width || 0) * (calc.height || 0) * qty;
                } else if (calc.method === 'volume') {
                    // calc.volume is in cbm or cft; convert to cm^3 (or in^3)
                    const factor = cmOrInches === 'cm' ? 100 * 100 * 100 : 12 * 12 * 12;
                    total += (calc.volume || 0) * factor * qty;
                }
            }
            return total;
        }
        if (product.volume) {
            // `volume` is m^3 for sea/LCL cargo, but on an air product it is stated in the same
            // system as its dimensions — cbm alongside cm, cubic feet alongside inches — so an
            // inches air product needs the cft factor, not the m^3 one.
            const qty = _.isUndefined(product.quantity) ? 1 : product.quantity;
            const isAir = product.type === 'Air freight' || product.type === 'Air Freight';
            const factor = isAir && (product.cmOrInches || 'cm') !== 'cm' ? 12 * 12 * 12 : 100 * 100 * 100;
            return qty * product.volume * factor;
        }
        return product.length * product.height * product.width * (_.isUndefined(product.quantity) ? 1 : product.quantity);
    })();


    if (_.isNaN(volume)) {
        return { val: 0, postfix: product.kgOrLbs || 'kg' };
    }

    const postfix = product.kgOrLbs || 'kg';

    let cmOrInches = product.cmOrInches;
    let kgOrLbs = product.kgOrLbs;
    if (product.type === 'Air Freight' || product.type === 'Air freight') {
        cmOrInches = cmOrInches || 'cm';
        kgOrLbs = kgOrLbs || 'kg';
    }

    if (cmOrInches === 'cm' && kgOrLbs === 'kg') {
        return { val: round ? Math.ceil(volume / 6000 * 2) / 2 : volume / 6000, postfix };
    }
    if (cmOrInches === 'cm' && kgOrLbs !== 'kg') {
        return { val: round ? Math.ceil(volume / 2721 * 2) / 2 : volume / 2721, postfix };
    }
    if (cmOrInches !== 'cm' && kgOrLbs === 'kg') {
        return { val: round ? Math.ceil(volume / 366 * 2) / 2 : volume / 366, postfix };
    }
    if (cmOrInches !== 'cm' && kgOrLbs !== 'kg') {
        return { val: round ? Math.ceil(volume / 166 * 2) / 2 : volume / 166, postfix };
    }
    return { val: 0, postfix };
}

function calcChargeableWeight({ product, transportationMethod = 'air', allProducts, round = false }) {
    // Chargeable weight belongs to the product, not to the leg. An 'Air freight' product also rides
    // a land leg — the airport↔door drayage the metro tariff prices per kg — and there it still has
    // to measure in chargeable kg. Returning null for it would leave getMultiplier with a 0
    // multiplier and price every per-kg charge at nothing, silently. Land only: an air product on a
    // sea quote stays without a chargeable weight, and every other product type keeps the old
    // behaviour of having none outside air.
    const isAirProduct = product?.type === 'Air freight' || product?.type === 'Air Freight';
    if (transportationMethod !== 'air' && !(isAirProduct && transportationMethod === 'land')) {
        return null;
    }

    // New unified "Air freight" with calculations array
    if (product.type === 'Air freight') {
        const calcs = product.calculations || [];
        if (calcs.length === 0) {
            return { val: 0, postfix: product.kgOrLbs || 'kg', title: 'Chargeable Weight' };
        }

        // Chargeable weight = max(total volume weight, total gross weight) across all calculations,
        // plus any explicit chargeable-weight method calcs added on top.
        let totalVolumeWeight = 0;
        let totalGrossWeight = 0;
        let totalExplicitCW = 0;
        for (const calc of calcs) {
            const qty = calc.quantity || 1;
            if (calc.method === 'chargeable-weight') {
                totalExplicitCW += (calc.chargeableWeight || 0) * qty;
            } else {
                // Hand the calculation straight back to calcVolumeWeight as a one-calculation
                // 'Air freight' product, so it goes through the same branch the product itself
                // would. Wrapping it as a legacy 'Air Freight' product instead used to send it
                // down the generic path, which converts a `volume` as cbm regardless of the
                // product's unit — so cft volumes on an inches product came out 1728/1000000 of
                // the size they should be, i.e. 578x too heavy.
                const vw = calcVolumeWeight({
                    product: {
                        kgOrLbs: product.kgOrLbs || 'kg',
                        cmOrInches: product.cmOrInches || 'cm',
                        type: 'Air freight',
                        calculations: [calc],
                    },
                    transportationMethod: 'air',
                });
                totalVolumeWeight += vw.val || 0;
                totalGrossWeight += (calc.weight || 0) * qty;
            }
        }

        let total = Math.max(totalVolumeWeight, totalGrossWeight) + totalExplicitCW;

        if (product.roundChargeableWeight !== false) {
            total = Math.ceil(total * 2) / 2;
        }

        return { val: total, postfix: product.kgOrLbs || 'kg', title: 'Chargeable Weight' };
    }

    const calculationType = _product => {
        if (_product.width && _product.height && _product.length) return 'dimensions';
        if (_product.volume) return 'volume';
        return 'chargeable-weight';
    };

    if (calculationType(product) === 'chargeable-weight') {
        if (round) {
            const chargeableWeight = product.chargeableWeight || 0;
            const rounded = Math.ceil(chargeableWeight * (product.quantity || 1) * 2) / 2;
            return { val: rounded, postfix: product.kgOrLbs || 'kg', title: 'Chargeable Weight' }
        }
        return { val: (product.chargeableWeight || 0) * (product.quantity || 1), postfix: product.kgOrLbs || 'kg', title: 'Chargeable Weight' };
    }

    // Case: AIR FREIGHT + BY-DIMENSIONS

    const allByDimensionsProducts = allProducts?.filter(p => (['dimensions', 'volume'].includes(calculationType(p))));
    if (_.isEmpty(allByDimensionsProducts)) return { val: 0, postfix: 'kg' };
    const base = calcVolumeWeight({ product: allByDimensionsProducts[0], transportationMethod, round }); 
    const productIndex = allByDimensionsProducts.findIndex(p => p.id === product.id);

    const allVolumeWeights = allByDimensionsProducts.map(p => {
        const weight = calcVolumeWeight({ product: p, transportationMethod, round })
        if (weight.postfix === base.postfix) return weight.val;
        if (weight.postfix === 'kg') return weight.val * 2.205; // All in pound
        return weight.val / 2.205; // All in KG
    }); 

    const allWeights = allByDimensionsProducts.map(p => {
        const weight = p.weight || 0;
        const quantity = p.quantity || 1;
        if ((p.kgOrLbs || 'kg') === base.postfix) return quantity * weight;
        if ((p.kgOrLbs || 'kg') === 'kg') return quantity * weight * 2.205; // All in pound
        return quantity * weight / 2.205; // All in KG
    });

    if (_.sum(allVolumeWeights) > _.sum(allWeights)) {
        if (round) {
            const rounded = Math.ceil(_.sum(allVolumeWeights) * 2) / 2;
            return { val: productIndex > 0 ? 0 : rounded, postfix: base.postfix, title: 'Chargeable Weight' }
        }
        return { val: productIndex >= 0 ? (allVolumeWeights[productIndex] || 0) : 0, postfix: base.postfix, title: 'Chargeable Weight' }
    } else {
        if (round) {
            const rounded = Math.ceil(_.sum(allWeights) * 2) / 2;
            return { val: productIndex > 0 ? 0 : rounded, postfix: base.postfix, title: 'Chargeable Weight' }
        }
        return { val: productIndex >= 0 ? (allWeights[productIndex] || 0) : 0, postfix: base.postfix, title: 'Chargeable Weight' }
    }
}

function calcTotalVolume({ product }) {
    if (product.type === 'LCL') {
        const divisor = (product.cmOrInches || 'cm') === 'cm' ? 1000000 : 1728;
        const postfix = (product.cmOrInches || 'cm') === 'cm' ? 'cbm' : 'cft';
        let total = 0;
        for (const calc of (product.calculations || [])) {
            const qty = calc.quantity || 1;
            if (calc.method === 'dimensions') {
                total += (calc.length || 0) * (calc.width || 0) * (calc.height || 0) / divisor * qty;
            } else {
                total += (calc.volume || 0) * qty;
            }
        }
        return { val: total, postfix };
    }

    if (product.type === 'Air freight') {
        const divisor = (product.cmOrInches || 'cm') === 'cm' ? 1000000 : 1728;
        const postfix = (product.cmOrInches || 'cm') === 'cm' ? 'cbm' : 'cft';
        let total = 0;
        for (const calc of (product.calculations || [])) {
            const qty = calc.quantity || 1;
            if (calc.method === 'dimensions') {
                total += (calc.length || 0) * (calc.width || 0) * (calc.height || 0) / divisor * qty;
            } else if (calc.method === 'volume') {
                total += (calc.volume || 0) * qty;
            }
        }
        return { val: total, postfix };
    }

    const calculationType = (() => {
        if (product.type === 'Air Freight') {
            if (product.width && product.height && product.length) return 'dimensions';
            if (product.volume) return 'volume';
        }
        if (product.type.startsWith('By Volume or Weight')) return 'volume';
        if (product.type.startsWith('By Dimensions or Weight')) return 'dimensions';
        if (product.type === 'By Dimensions') return 'dimensions';
        return 'chargeable-weight';
    })();

    if (calculationType === 'volume') {
        return { val: product.volume * (product.quantity || 1), postfix: 'cbm'  };
    }

    if (calculationType === 'dimensions') {
        if (product.cmOrInches === 'cm') {
            return {
                val: product.quantity * product.length * product.width * product.height / 1000000,
                postfix: 'cbm'
            }
        } else {
            return {
                val: product.quantity * product.length * product.width * product.height / 1728,
                postfix: 'cft'
            }
        }
    }

    return { val: 0, postfix: 'cbm' };
}

function calcWeightOrMeasurment({ product, transportationMethod, allProducts }) {
    if (product.type === 'LCL') {
        const totalVolume = calcTotalVolume({ product });
        let totalWeightKg = 0;
        for (const calc of (product.calculations || [])) {
            totalWeightKg += (calc.weight || 0) * (calc.quantity || 1);
        }
        // Convert weight to kg if in lbs
        if ((product.kgOrLbs || 'kg') !== 'kg') {
            totalWeightKg = totalWeightKg * 0.453592;
        }
        const totalTons = totalWeightKg / 1000;
        const totalCbm = totalVolume.postfix === 'cft'
            ? totalVolume.val * 0.0283168
            : totalVolume.val;

        // densityRatio: how many kg equal 1 CBM (default 1000)
        // Compare: actual weight (kg) vs volume (cbm) * densityRatio
        const densityRatio = product.densityRatio || 1000;
        if (totalWeightKg > totalCbm * densityRatio) {
            return { val: totalTons, postfix: 'ton', title: 'Weight (Weight or Measurement)' };
        } else {
            return { val: totalCbm, postfix: 'cbm', title: 'Volume (Weight or Measurement)' };
        }
    }

    const WM_TYPES = [
        'By Volume or Weight (sea)',
        'By Dimensions or Weight (sea)',
        // Land W/M variants — same calc, just labelled per transportation mode.
        'By Dimensions or Weight (land)',
        'By Volume or Weight (land)',
        'By Dimensions',
    ];
    const wmProducts = allProducts.filter(p => WM_TYPES.includes(p.type));
    const productIndex = wmProducts.findIndex(p => p.id === product.id);
    if (productIndex === -1) return null;

    const allWeightsInTons = wmProducts.map(p => {
        return (p.kgOrLbs === 'kg' || p.type === 'By Volume or Weight (sea)' || p.type === 'By Volume or Weight (land)') ?
            (_.isUndefined(p.quantity) ? 1 : p.quantity) * p.weight / 1000 :
            (_.isUndefined(p.quantity) ? 1 : p.quantity) * p.weight * 0.000453592; /* lb -> ton */
    });

    const allVolumesInCbm = wmProducts.map(p => {
        const totalVolume = calcTotalVolume({ product: p });
        return totalVolume.postfix === 'cbm' ? totalVolume.val : totalVolume.val * 0.0283168 /* cft -> cbm */
    });

    if (_.sum(allWeightsInTons) > _.sum(allVolumesInCbm)) {
        return { val: allWeightsInTons[productIndex], postfix: 'ton', title: 'Weight (Weight or Measurement)' }
    } else {
        return { val: allVolumesInCbm[productIndex], postfix: 'cbm', title: 'Volume (Weight or Measurement)' }
    }
}

function getMultiplier({ product, transportationMethod, allProducts, shorten, round = false}) {
    const [multiplier, multiplierText] = (() => {

        if (product.type === 'Air Freight' || product.type === 'Air freight') {
            // For Air freight rate products with default placeholder, show unit
            if (product.type === 'Air freight' && product.calculations?.length === 1) {
                const c = product.calculations[0];
                const isDefault = (c.chargeableWeight === 1 && c.weight === 1 && (c.quantity || 1) === 1);
                if (isDefault) return [1, product.kgOrLbs || 'kg'];
            }
            const w = calcChargeableWeight({ product, transportationMethod, allProducts, round });
            return [w?.val || 0, w?.postfix || 'kg']
        }

        const WM_PRODUCT_TYPES = [
            'LCL',
            'By Dimensions or Weight (sea)',
            'By Volume or Weight (sea)',
            // Land W/M variants — same W/M semantics, just labelled per mode.
            'By Dimensions or Weight (land)',
            'By Volume or Weight (land)',
            'By Dimensions',
        ];
        if (WM_PRODUCT_TYPES.includes(product.type) || product.type.startsWith('By Dimensions or Weight (sea)')) {
            // For LCL rate products with no real cargo data, show W/M
            // Detect by checking if calculations only have the default placeholder values (volume=1, weight=1000)
            if (product.type === 'LCL' && product.calculations?.length === 1) {
                const c = product.calculations[0];
                const isDefault = (c.volume === 1 && c.weight === 1000 && (c.quantity || 1) === 1);
                if (isDefault) return [1, 'W/M'];
            }
            const w = calcWeightOrMeasurment({ product, transportationMethod, allProducts });
            if (!w) return [0, 'ton'];

            if (w.postfix === 'kg') {
                return [w.val / 1000, 'ton'];
            }
            return [w.val, w.postfix]
        }

        return [product.quantity, 'container(s)']

    })();
    return { multiplier, multiplierText };
}

export { calcWeight, calcVolumeWeight, calcTotalVolume, calcChargeableWeight, calcWeightOrMeasurment, getMultiplier };
